import React, { useMemo, FC } from 'react';
import { Table, useTable, Badge } from 'portal-commons';

import { brandController, BrandVettingRecord } from '@/apis/core';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';
import { STATUS_BADGE_COLOR_MAP } from '@/constants/enum-mapping';
import ScoreTooltip from '../../../components/VettingDetailsListingRow/ScoreTooltipV2';

const TABLE_HEADERS = [
  {
    id: 'vettingClass',
    label: 'Verification Type',
    sortable: false,
  },
  {
    id: 'vettedDate',
    label: 'Verified On',
    sortable: false,
  },
  {
    id: 'vettingStatus',
    label: 'Verification Status',
    sortable: false,
  },
];

const Auth2faTab: FC<{ brandUid: string }> = ({ brandUid }) => {
  const table = useTable<BrandVettingRecord>(
    async () => {
      const records = brandUid
        ? (await brandController.getVettings(brandUid, {
            vettingStatus: 'ACTIVE',
          })) || []
        : [];

      return {
        data: records.filter((v) => v.vettingClass === 'AUTHPLUS'),
        total: records.length,
        page: 1,
        rowsPerPage: Math.max(records.length, 10),
      };
    },
    {
      deps: [brandUid],
      inMemory: true,
    }
  );

  const tableDisplayData = useMemo(() => {
    return table.tableData.map((d) => ({
      vettingClass: (
        <span data-testid="vettingClass">{d.vettingClass || 'N/A'}</span>
      ),
      vettingStatus: d.vettingStatus ? (
        <Badge
          size="md"
          shape="rectangle"
          variant="fill"
          type={STATUS_BADGE_COLOR_MAP[d.vettingStatus] || 'info'}
          endAdornment={<ScoreTooltip data={d} />}
          data-testid="vettingStatus"
        >
          {humanize(d.vettingStatus)}
        </Badge>
      ) : (
        <span data-testid="vettingStatus">N/A</span>
      ),
      vettedDate: (
        <span data-testid="vettedDate">
          {convertTimeWithTimezone(
            d.vettedDate,
            undefined,
            'MM/DD/YYYY - hh:mm z'
          ) || 'N/A'}
        </span>
      ),
    }));
  }, [table.tableData]);

  return (
    <Table
      emptyBlockHeight="180px"
      loading={false}
      headRows={TABLE_HEADERS}
      emptyState="no vettings to view"
      disableHover={true}
      data={tableDisplayData}
      data-testid="auth2faTable"
    />
  );
};

export default Auth2faTab;
